import { blockpotTokenAbi } from '@/abi/blockPotTokenAbi'
import { contributorsRewardTrackerAbi } from '@/abi/contributorsRewardTrackerAbi'
import { lotteryAbi } from '@/abi/lotteryAbi'
import { wethAbi } from '@/abi/wethAbi'
import { ContractName, getContractAddress } from '@/constants/contract-addresses'
import useReadContract from '@/hooks/contracts/read/useReadContract'
import { useTriggerOnChanged } from '@/hooks/utilities/useChanged'
import useEventBlockNumber from '@/hooks/utilities/useEventsBlockNumber'
import { useQueryClient } from '@tanstack/react-query'
import React, {
    createContext, useContext, useEffect, useState,
} from 'react'
import { useAccount, useBlockNumber, useChainId } from 'wagmi'
import { useSelectedGame } from './SelectedGameProvider'
import { usePrevious } from '@/hooks/utilities/usePrevious'
import { blockpotConfigManagerAbi } from '@/abi/blockPotConfigManagerAbi'

type BlockPotEventsContextType = {
    lotteryRoundBlockNumber: bigint
}

const BlockPotEventsContext = createContext<BlockPotEventsContextType>({
    lotteryRoundBlockNumber: 0n
})

type Props = {
    children: React.ReactNode
}

export default function BlockPotEventsProvider({ children }: Props): React.ReactElement {
    const chainId = useChainId()
    const queryClient = useQueryClient()
    const { gameContractName } = useSelectedGame()

    const lottery = useReadContract(gameContractName, lotteryAbi)
    const blockPotToken = useReadContract(ContractName.BLOCK_POT_TOKEN, blockpotTokenAbi)
    const weth = useReadContract(ContractName.WETH, wethAbi)
    const contributorsRewardTracker = useReadContract(ContractName.CONTRIBUTORS_REWARD_TRACKER, contributorsRewardTrackerAbi)

    const { address } = useAccount()
    const { data: blockNumber } = useBlockNumber({ watch: true })
    const [playerEntriesBlockNumber, setPlayerEntriesBlockNumber] = useState(0n)
    const [entriesBlockNumber, setEntriesBlockNumber] = useState(0n)
    const [blockPotTokenBlockNumber, setBlockPotTokenBlockNumber] = useState(0n)
    const [contributorsBlockNumber, setContributorsBlockNumber] = useState(0n)
    const [wethBlockNumber, setWETHBlockNumber] = useState(0n)
    const [lotteryRoundBlockNumber, setLotteryRoundBlockNumber] = useState(0n)
    const [showDebug, setShowDebug] = useState(false)

    // Reset state when selected game changes
    const previousSelectedGame = usePrevious(gameContractName)
    useEffect(() => {
        if (previousSelectedGame && previousSelectedGame !== gameContractName) { 
            setPlayerEntriesBlockNumber(0n)
            setEntriesBlockNumber(0n)
            setLotteryRoundBlockNumber(0n)
        }
    }, [gameContractName, previousSelectedGame])

    // Unfiltered Event Listening

    const governanceBlockNumber = useEventBlockNumber({
        address: getContractAddress(chainId, ContractName.CONFIG_MANAGER),
        abi: blockpotConfigManagerAbi,
        events: ['ProposalCreated', 'ProposalClosed']
    })

    const voteBlockNumber = useEventBlockNumber({
        address: getContractAddress(chainId, ContractName.CONFIG_MANAGER),
        abi: blockpotConfigManagerAbi,
        events: ['ProposalVote']
    })

    // Lottery Event Watchers
    useEffect(() => {
        const unwatchOnEntry = lottery.watchEvent.LotteryOnEntry({
            onLogs: (logs) => {
                const shouldInvalidate = logs.some((l) => l.args.player === address)
                if (shouldInvalidate) {
                    setPlayerEntriesBlockNumber(logs[logs.length - 1].blockNumber ?? 0n)
                }
                if (logs.length > 0) {
                    setEntriesBlockNumber(logs[logs.length - 1].blockNumber)
                }
            }
        })
        const unwatchOnDrawnNumbersReceived = lottery.watchEvent.LotteryDrawnNumbersReceived(
            {},
            {
                onLogs: (logs) => {
                    setLotteryRoundBlockNumber(logs[logs.length - 1].blockNumber ?? 0n)
                }
            }
        )

        const unwatchOnDrawingNumbers = lottery.watchEvent.LotteryDrawingNumbers({
            onLogs: (logs) => {
                setLotteryRoundBlockNumber(logs[logs.length - 1].blockNumber ?? 0n)
            }
        })

        const unwatchOnWinnerSelected = lottery.watchEvent.LotteryWinnerSelected(
            {
                onLogs: (logs) => {
                    setLotteryRoundBlockNumber(logs[logs.length - 1].blockNumber ?? 0n)
                }
            }
        )

        const unwatchOnNoWinner = lottery.watchEvent.LotteryNoWinner({
            onLogs: (logs) => {
                setLotteryRoundBlockNumber(logs[logs.length - 1].blockNumber ?? 0n)
            }
        })

        return () => {
            console.log(`Unwatching lottery events for ${lottery.address}`)
            unwatchOnEntry()
            unwatchOnDrawingNumbers()
            unwatchOnNoWinner()
            unwatchOnDrawnNumbersReceived()
            unwatchOnWinnerSelected()
        }
    }, [lottery.address, address]) // eslint-disable-line react-hooks/exhaustive-deps

    // Block Pot Token Event Watchers
    useEffect(() => {
        const unwatchApproval = blockPotToken.watchEvent.Approval({ owner: address }, {
            onLogs: (logs) => {
                if (logs.some(l => l.args.owner === address)) {
                    setBlockPotTokenBlockNumber(logs[logs.length - 1].blockNumber)
                }
            }
        })

        const unwatchTransferFrom = blockPotToken.watchEvent.Transfer({ from: address }, {
            onLogs: (logs) => {
                setBlockPotTokenBlockNumber(logs[logs.length - 1].blockNumber)
            }
        })

        const unwatchTransferTo = blockPotToken.watchEvent.Transfer({ to: address }, {
            onLogs: (logs) => {
                setBlockPotTokenBlockNumber(logs[logs.length - 1].blockNumber)
            }
        })

        return () => {
            unwatchApproval()
            unwatchTransferFrom()
            unwatchTransferTo()
        }
    }, [blockPotToken.address, address]) // eslint-disable-line react-hooks/exhaustive-deps

    // WETH Event Watchers
    useEffect(() => {
        const unwatchWithdrawal = weth.watchEvent.Withdrawal({ src: address }, {
            onLogs: (logs) => {
                setWETHBlockNumber(logs[logs.length - 1].blockNumber)
            }
        })

        const unwatchDeposit = weth.watchEvent.Deposit({ dst: address }, {
            onLogs: (logs) => {
                setWETHBlockNumber(logs[logs.length - 1].blockNumber)
            }
        })

        return () => {
            unwatchWithdrawal()
            unwatchDeposit()
        }
    }, [weth.address, address]) // eslint-disable-line react-hooks/exhaustive-deps

    // Contributors Rewards Event Watchers
    useEffect(() => {
        const unwatchClaim = contributorsRewardTracker.watchEvent.Claim({
            onLogs: (logs) => {
                setContributorsBlockNumber(logs[logs.length - 1].blockNumber)
            }
        })

        const unwatchTransfer = contributorsRewardTracker.watchEvent.Transfer({ from: address, to: address }, {
            onLogs: (logs) => {
                setContributorsBlockNumber(logs[logs.length - 1].blockNumber)
            }
        })

        return () => {
            unwatchClaim()
            unwatchTransfer()
        }
    }, [contributorsRewardTracker.address, address]) // eslint-disable-line react-hooks/exhaustive-deps

    // Triggers
    useTriggerOnChanged(blockPotTokenBlockNumber, () => {
        queryClient.invalidateQueries({ queryKey: ['blockPotToken'] })
        queryClient.invalidateQueries({ queryKey: ['votingPower'] })
    })

    useTriggerOnChanged(wethBlockNumber, () => {
        queryClient.invalidateQueries({ queryKey: ['erc20', ContractName.WETH] })
    })

    //// Lottery
    useTriggerOnChanged(playerEntriesBlockNumber, async () => {
        let promises: Promise<void>[] = []
        promises.push(queryClient.invalidateQueries({ queryKey: ['currentRoundEntryIndexes'] }))
        promises.push(queryClient.invalidateQueries({ queryKey: ['roundPurchases'] }))
        promises.push(queryClient.invalidateQueries({ queryKey: ['playerStats'] }))
        await Promise.all(promises)
        await queryClient.refetchQueries({ queryKey: ['currentRoundEntryIndexes'] })
        queryClient.refetchQueries({ queryKey: ['roundPurchases'] })
        queryClient.refetchQueries({ queryKey: ['playerStats'] })
    })

    useTriggerOnChanged(entriesBlockNumber, () => {
        queryClient.invalidateQueries({ queryKey: ['affiliateStats'] })
        queryClient.invalidateQueries({ queryKey: ['lotteryState'] })
        queryClient.invalidateQueries({ queryKey: ['contributorInfo'] })
        queryClient.invalidateQueries({ queryKey: ['balanceAllocations'] })
    })

    useTriggerOnChanged(lotteryRoundBlockNumber, () => {
        queryClient.invalidateQueries({ queryKey: ['lotteryState'] })
        queryClient.invalidateQueries({ queryKey: ['contributorInfo'] })
        queryClient.invalidateQueries({ queryKey: ['nextConfig'] })
        queryClient.invalidateQueries({ queryKey: ['blockPotRewardTracker'] })
    })

    //// Governance
    useTriggerOnChanged(governanceBlockNumber, () => {
        queryClient.invalidateQueries({ queryKey: ['activeProposalTypes'] })
        queryClient.invalidateQueries({ queryKey: ['proposals'] })
        queryClient.invalidateQueries({ queryKey: ['canUnstakeBPT'] })
    })

    useTriggerOnChanged(voteBlockNumber, () => {
        queryClient.invalidateQueries({ queryKey: ['canUnstakeBPT'] })
        queryClient.invalidateQueries({ queryKey: ['proposals'] })
    })

    //// Contributors
    useTriggerOnChanged(contributorsBlockNumber, () => {
        queryClient.invalidateQueries({ queryKey: ['contributorInfo'] })
    })

    // TODO: Move this elsewhere, too many re-renders
    useTriggerOnChanged(blockNumber, () => {
        queryClient.invalidateQueries({ queryKey: ['balance'] })
    })

    return (
        <BlockPotEventsContext.Provider value={{
            lotteryRoundBlockNumber
        }}>
            <div className='w-full h-full relative'>
                {
                    !import.meta.env.PROD && (
                        <div className='absolute top-1 left-1 bg-background border border-border rounded-md p-2 z-10 text-xs font-mono cursor-pointer' onClick={() => setShowDebug(prev => !prev)}>
                            <p className='font-bold'>Block Numbers</p>
                            {
                                showDebug && (
                                    [
                                        `blockNumber: ${blockNumber}`,
                                        `lotteryRoundBlockNumber: ${lotteryRoundBlockNumber}`,
                                        `governanceBlockNumber: ${governanceBlockNumber}`,
                                        `voteBlockNumber: ${voteBlockNumber}`,
                                        `contributorsBlockNumber: ${contributorsBlockNumber}`,
                                        `wethBlockNumber: ${wethBlockNumber}`,
                                        `blockPotTokenBlockNumber: ${blockPotTokenBlockNumber}`,
                                        `playerEntriesBlockNumber: ${playerEntriesBlockNumber}`,
                                        `entriesBlockNumber: ${entriesBlockNumber}`,
                                    ]
                                        .toSorted()
                                        .map((line) => (
                                            <p key={line}>{line}</p>
                                        ))
                                )
                            }
                        </div>
                    )}
                {children}
            </div>
        </BlockPotEventsContext.Provider>
    )
}

export const useBlockPotEvents = () => {
    return useContext(BlockPotEventsContext)
}